import { Box, Popover, Typography, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useGetNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
} from '@/store/api/notificationsApi';
import type { NotificationItem as NotificationItemType } from '@/store/api/notificationsApi';
import { useWsFallbackPolling } from '@/hooks/useWsFallbackPolling';
import { NotificationItem } from './NotificationItem';

// TODO: Add "load more" / "view all" affordance for users with >50 notifications
const SOURCE_TYPE_ROUTES: Record<string, string> = {
  qc_result: '/qc',
  alert_event: '/monitoring',
};

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export function NotificationPopover({ anchorEl, open, onClose }: Props) {
  const navigate = useNavigate();
  const fallback = useWsFallbackPolling();
  const { data } = useGetNotificationsQuery(undefined, { ...fallback });
  const [markRead] = useMarkReadMutation();
  const [markAllRead] = useMarkAllReadMutation();

  const notifications: NotificationItemType[] = data?.items ?? [];

  const handleItemClick = (notification: NotificationItemType) => {
    void markRead(notification.id);
    const route = SOURCE_TYPE_ROUTES[notification.source_type];
    if (route) {
      void navigate(route);
    }
    onClose();
  };

  const handleMarkAllRead = () => {
    void markAllRead(undefined);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Notifications
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={handleMarkAllRead}
          sx={{ fontSize: '0.75rem', textTransform: 'none', minWidth: 'auto' }}
        >
          Mark all read
        </Button>
      </Box>

      <Divider />

      {/* Notification list */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {notifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => (
            <NotificationItem key={n.id} notification={n} onClick={handleItemClick} />
          ))
        )}
      </Box>
    </Popover>
  );
}
